import React, { useState } from "react";
import { X, Loader2, UserPen, Mail } from "lucide-react";
import { toast } from "sonner";
import { reportsApi } from "../lib/events";
import { extractError } from "../lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * EditAttendeeModal — correct the attendee name/email on an already-issued
 * ticket. Changing the email re-sends the same ticket to the new address once.
 * Never changes the order, buyer, totals, seats, QR or check-in state.
 */
export function EditAttendeeModal({ eventId, ticket, onClose, onSaved }) {
  const [name, setName] = useState(ticket?.attendee_name || ticket?.buyer_name || "");
  const [email, setEmail] = useState(ticket?.attendee_email || "");
  const [busy, setBusy] = useState(false);

  if (!ticket) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return; // single-flight guard against duplicate submits
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      toast.error("Attendee name cannot be blank.");
      return;
    }
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const payload = { attendee_name: trimmedName };
      if (trimmedEmail) payload.attendee_email = trimmedEmail;
      const res = await reportsApi.editAttendee(eventId, ticket.id, payload);
      toast.success(res.message || "Attendee updated successfully.");
      onSaved?.(res);
      onClose?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="edit-attendee-modal"
    >
      <div
        className="bg-white border-2 border-[var(--gt-ink)] shadow-[8px_8px_0px_0px_var(--gt-ink)] w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-[var(--gt-ink)]">
          <div>
            <div className="gt-label flex items-center gap-1"><UserPen size={13} /> Edit attendee</div>
            <h3 className="font-heading text-2xl font-black tracking-tighter mt-1">Correct details</h3>
            <div className="text-xs text-[var(--gt-ink-3)] mt-1 font-mono">Ticket {ticket.code}</div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"
            data-testid="edit-attendee-close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="edit-attendee-form">
          <div>
            <label className="gt-label mb-1 block">Attendee name</label>
            <input
              className="gt-input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              data-testid="edit-attendee-name-input"
            />
          </div>
          <div>
            <label className="gt-label mb-1 block">Attendee email</label>
            <input
              type="email"
              className="gt-input w-full"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              data-testid="edit-attendee-email-input"
            />
            <p className="text-xs text-[var(--gt-ink-3)] mt-1 flex items-start gap-1">
              <Mail size={12} className="mt-0.5 shrink-0" />
              Changing the email re-sends this ticket to the new address. The QR
              code, seat and check-in stay the same.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="gt-btn-secondary"
              data-testid="edit-attendee-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="gt-btn-primary inline-flex items-center gap-2"
              data-testid="edit-attendee-save"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditAttendeeModal;
